import { utils, writeFile } from "xlsx"

import { formatCellForExport } from "./cellFormat"

/**
 * Export du tableau de drill-down en CSV / XLSX.
 *
 * ⚠️ Ce module N'UTILISE PAS `Utils/global.exportDatas`, et c'est délibéré. Ce helper historique
 * a deux défauts rédhibitoires pour cette donnée-ci :
 * - son CSV fait un `row.join(";")` sans échappement → toute cellule contenant `;`, un guillemet
 *   ou un saut de ligne (banal sur un libellé GRC) décale la ligne entière ;
 * - son XLSX passe par `json_to_sheet`, indexé par LIBELLÉ de colonne → deux colonnes homonymes
 *   s'écrasent l'une l'autre. Le drill-down en produit couramment (« Libellé » du référentiel
 *   principal et « Libellé » du référentiel lié).
 *
 * On construit donc la feuille en `aoa_to_sheet` (tableau de tableaux : l'ordre et les doublons
 * de libellés sont préservés) et on laisse SheetJS écrire le CSV, qui échappe correctement.
 * C'est la même stratégie que `Shared/webworkers/exportSerialize.ts`.
 */

export interface ExportColumn {
  uuid: string
  name: string
  /** Clé réelle dans la ligne — peut différer de `uuid` (alias PostgreSQL tronqué à 63 car.). */
  dataKey: string
}

export type ExportCell = string | number | boolean

export interface ExportPayload {
  header: string[]
  body: ExportCell[][]
}

/** Le BOM UTF-8, sans lequel Excel lit les accents en mojibake. */
export const BOM_UTF8 = "\ufeff"

/**
 * Sépare les colonnes en CSV. `;` et non `,` : c'est ce qu'attendent les tableurs en locale
 * française, et une cellule multi-valeurs est déjà jointe par des virgules.
 */
const CSV_FIELD_SEPARATOR = ";"

/** Caractères interdits dans un nom de fichier Windows, plus les espaces. */
const FILENAME_FORBIDDEN = /[\/:*?"<>|\s]+/g

const FILENAME_MAX_LENGTH = 120

/**
 * Transforme les lignes brutes de l'ETL en `{ header, body }`.
 *
 * Seules les `columns` passées sont exportées, dans leur ordre : l'export reflète EXACTEMENT
 * les colonnes visibles du tableau. Une ligne à laquelle il manque une clé donne une cellule
 * vide, jamais un décalage.
 */
export function buildExportPayload(
  rows: Array<Record<string, any>>,
  columns: ExportColumn[],
  isDateColumn: (columnUuid: string) => boolean = () => false
): ExportPayload {
  const header = columns.map(col => col.name)
  const body = (rows ?? []).map(row =>
    columns.map(col => formatCellForExport(row?.[col.dataKey], isDateColumn(col.uuid)))
  )
  return { header, body }
}

/** Feuille de calcul commune au CSV et au XLSX — une seule construction, un seul comportement. */
export function buildSheet({ header, body }: ExportPayload) {
  return utils.aoa_to_sheet([header, ...body])
}

/** Rend le contenu CSV complet, BOM inclus. */
export function renderCsv(payload: ExportPayload): string {
  return BOM_UTF8 + utils.sheet_to_csv(buildSheet(payload), { FS: CSV_FIELD_SEPARATOR })
}

/**
 * Nom de fichier sûr, dérivé du titre du widget et daté.
 * Retombe sur `export` quand le titre ne laisse rien d'exploitable.
 */
export function buildExportFileName(title: string | undefined, dateStamp: string): string {
  const base = (title ?? "")
    .replace(FILENAME_FORBIDDEN, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, FILENAME_MAX_LENGTH)
  return `${base || "export"}_${dateStamp}`
}

export function downloadCsv(payload: ExportPayload, fileName: string): void {
  const blob = new Blob([renderCsv(payload)], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `${fileName}.csv`
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function downloadXlsx(payload: ExportPayload, fileName: string): void {
  const workbook = utils.book_new()
  utils.book_append_sheet(workbook, buildSheet(payload), "Export")
  writeFile(workbook, `${fileName}.xlsx`)
}
